import React, { useState, useRef, useEffect } from "react";
import { api, apiError, API, getToken } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";
import { Send, Download, Mail, Loader2 } from "lucide-react";
import { LogoMark } from "@/components/marca/Logo";
import { toast } from "sonner";
import { t, getIdioma } from "@/lib/i18n";

const SUGERENCIAS = [
  "¿Cómo viene el día?",
  "¿Cuántos contenedores movimos esta semana?",
  "Reporte de diésel del trimestre",
];

export default function Agente() {
  const [mensajes, setMensajes] = useState([]);
  const [input, setInput] = useState("");
  const [enviando, setEnviando] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [mensajes]);

  const actualizarUltimo = (cambios) => {
    setMensajes((prev) => {
      const copy = [...prev];
      copy[copy.length - 1] = { ...copy[copy.length - 1], ...cambios };
      return copy;
    });
  };

  const enviarMensaje = async (texto) => {
    const tt = (texto ?? input).trim();
    if (!tt || enviando) return;
    const historial = mensajes.slice(-10).map((m) => ({ role: m.role, content: m.content }));
    setInput("");
    setMensajes((prev) => [...prev, { role: "user", content: tt }, { role: "assistant", content: "" }]);
    setEnviando(true);
    try {
      const res = await fetch(`${API}/agente/mensaje`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}`, "X-Idioma": getIdioma() },
        body: JSON.stringify({ mensaje: tt, historial }),
      });
      if (!res.ok || !res.body) throw new Error(t("sin conexión"));
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let acumulado = "";
      while (true) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const partes = buffer.split("\n\n");
        buffer = partes.pop();
        for (const parte of partes) {
          const linea = parte.trim();
          if (!linea.startsWith("data: ")) continue;
          let ev;
          try { ev = JSON.parse(linea.slice(6)); } catch { continue; }
          if (ev.tipo === "delta") {
            acumulado += ev.texto;
            actualizarUltimo({ content: acumulado });
          } else if (ev.tipo === "reporte") {
            actualizarUltimo({ reporte: ev });
          } else if (ev.tipo === "correo") {
            toast[ev.enviado ? "success" : "error"](ev.enviado ? t("Correo enviado a {email}", { email: ev.email }) : (ev.motivo || t("No se pudo enviar")));
          } else if (ev.tipo === "error") {
            toast.error(ev.mensaje || t("Hubo un error consultando al agente."));
          }
        }
      }
    } catch {
      toast.error(t("Hubo un error consultando al agente. Prueba de nuevo."));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <div className="p-4 lg:p-8 flex flex-col h-[calc(100vh-3.5rem)] lg:h-screen max-w-[760px] mx-auto w-full">
      <div>
        <h1 className="font-display font-bold text-[26px] lg:text-[30px] leading-[1.05] text-ink" data-testid="page-title">{t("Agente")}</h1>
        <p className="text-ink-3 mt-1.5">{t("Pregúntale por cargas, choferes, diésel, incidentes o insumos. Es de solo lectura.")}</p>
      </div>

      <div data-testid="agente-mensajes" className="flex-1 overflow-y-auto my-4 space-y-4 bg-white rounded-xl border p-4">
        {mensajes.length === 0 && (
          <div className="py-10 text-center" data-testid="agente-vacio">
            <p className="font-display font-semibold text-[22px] text-ink">{t("Pregunta sobre cargas, choferes, diésel e insumos")}</p>
            <div className="flex flex-wrap justify-center gap-2 mt-4">
              {[t("¿Qué cargas siguen sin asignar hoy?"), t("¿Cuántos galones cargó cada chofer este mes?"), t("¿Hay problemas abiertos?")].map((q) => (
                <button key={q} type="button" onClick={() => enviarMensaje(q)} className="rounded-full border border-line-strong bg-white px-3 py-1.5 text-[13.5px] text-ink-2 hover:bg-surface-2">{q}</button>
              ))}
            </div>
          </div>
        )}
        {mensajes.map((m, i) => (
          <div key={i} data-testid={`agente-mensaje-${i}`} className={cn("flex gap-3", m.role === "user" && "justify-end")}>
            {m.role === "assistant" && <LogoMark size={26} className="shrink-0 mt-0.5" />}
            <div className={cn("max-w-[80%] rounded-xl px-4 py-2.5 text-sm whitespace-pre-wrap", m.role === "user" ? "bg-ink text-white" : "bg-transparent text-ink px-0")}>
              {m.content || (m.role === "assistant" && enviando && i === mensajes.length - 1 ? t("Pensando…") : "")}
              {m.reporte && (
                <div data-testid={`agente-reporte-${i}`} className="mt-3">
                  <ReporteAcciones reporte={m.reporte} idx={i} />
                </div>
              )}
            </div>
            
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {SUGERENCIAS.map((s) => (
          <button
            key={s}
            data-testid={`agente-sugerencia-${SUGERENCIAS.indexOf(s)}`}
            type="button"
            disabled={enviando}
            onClick={() => enviarMensaje(t(s))}
            className="text-xs font-medium bg-paper hover:bg-line rounded-full px-3 py-1.5 text-ink-2 transition-colors disabled:opacity-50"
          >
            {t(s)}
          </button>
        ))}
      </div>

      <form onSubmit={(e) => { e.preventDefault(); enviarMensaje(); }} className="flex gap-2">
        <Input
          data-testid="agente-chat-input"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={t("Pregunta algo, por ejemplo: ¿cómo viene el día?")}
          className="h-11"
          disabled={enviando}
        />
        <Button data-testid="agente-enviar-btn" type="submit" disabled={enviando || !input.trim()} className="h-11 bg-brand hover:bg-brand-dark text-white px-5">
          {enviando ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </form>
    </div>
  );
}

function ReporteAcciones({ reporte, idx }) {
  const [mostrarEmail, setMostrarEmail] = useState(false);
  const [email, setEmail] = useState("");
  const [enviando, setEnviando] = useState(false);

  const descargar = async () => {
    try {
      const res = await api.get(`/reportes/${reporte.reporte_id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = t("reporte_{reporte_tipo}.pdf", { reporte_tipo: reporte.reporte_tipo }); a.click();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiError(e)); }
  };

  const enviar = async () => {
    if (!email.trim()) { toast.error(t("Ingresa un correo")); return; }
    setEnviando(true);
    try {
      const { data } = await api.post(`/reportes/${reporte.reporte_id}/enviar`, { email: email.trim() });
      if (data.enviado) { toast.success(t("Enviado a {v0}", { v0: email.trim() })); setMostrarEmail(false); setEmail(""); }
      else toast.error(data.motivo || t("Correo no configurado"));
    } catch (e) { toast.error(apiError(e)); } finally { setEnviando(false); }
  };

  return (
    <div className="flex flex-wrap items-center gap-2">
      <Button data-testid={`agente-descargar-pdf-${idx}`} size="sm" variant="outline" className="h-8 bg-white" onClick={descargar}>
        <Download className="h-3.5 w-3.5 mr-1" />{t(" Descargar PDF")}</Button>
      {!mostrarEmail ? (
        <Button data-testid={`agente-mostrar-correo-${idx}`} size="sm" variant="outline" className="h-8 bg-white" onClick={() => setMostrarEmail(true)}>
          <Mail className="h-3.5 w-3.5 mr-1" />{t(" Enviar por correo")}</Button>
      ) : (
        <div className="flex items-center gap-1.5">
          <Input data-testid={`agente-email-input-${idx}`} value={email} onChange={(e) => setEmail(e.target.value)} placeholder={t("correo@empresa.com")} className="h-8 w-48 bg-white text-xs" />
          <Button data-testid={`agente-enviar-correo-${idx}`} size="sm" className="h-8 bg-brand hover:bg-brand-dark text-white" onClick={enviar} disabled={enviando}>
            {enviando ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : t("Enviar")}
          </Button>
        </div>
      )}
    </div>
  );
}
